import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

import {
  namespaces,
  useSharedTranslation,
} from 'us-react-components/dist/hooks/useSharedTranslation';
import {
  axios,
  Sitter,
  SitterExperience,
  SitterService,
  YearExperience,
  YearExperienceService,
} from 'us-web-services';

import { Button, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';

const NOT_WILLING_ID = 8;
const SITTER_DOG_BG =
  'https://assets.urbansitter.com/static/img/backgrounds/sitter-dog.webp';

type AgeKey = 'infant' | 'toddler' | 'school';

const AGE_KEYS: Array<{ key: AgeKey; labelKey: string; rangeKey: string }> = [
  { key: 'infant', labelKey: 'pages.worksWith.infant', rangeKey: 'pages.worksWith.infantRange' },
  { key: 'toddler', labelKey: 'pages.worksWith.toddler', rangeKey: 'pages.worksWith.toddlerRange' },
  { key: 'school', labelKey: 'pages.worksWith.school', rangeKey: 'pages.worksWith.schoolRange' },
];

type PetOrHomeField =
  | 'willSitDog'
  | 'willSitCat'
  | 'willSitOtherPet'
  | 'willSitSenior'
  | 'willSitHome';

const EXTRA_TOGGLES: Array<{ field: PetOrHomeField; labelKey: string }> = [
  { field: 'willSitDog', labelKey: 'pages.worksWith.willSitDog' },
  { field: 'willSitCat', labelKey: 'pages.worksWith.willSitCat' },
  { field: 'willSitOtherPet', labelKey: 'pages.worksWith.willSitOtherPet' },
  { field: 'willSitSenior', labelKey: 'pages.worksWith.willSitSenior' },
  { field: 'willSitHome', labelKey: 'pages.worksWith.willSitHome' },
];

export const WorksWith = observer((): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const navigate = useNavigate();
  const { stores } = useDependencies();
  const sitter = stores.sitter.sitter as Sitter;

  const [experiences, setExperiences] = useState<Partial<SitterExperience>>(
    (sitter?.experiences ?? {}) as Partial<SitterExperience>,
  );
  const [toggles, setToggles] = useState<Record<PetOrHomeField, boolean>>({
    willSitDog: !!sitter?.willSitDog,
    willSitCat: !!sitter?.willSitCat,
    willSitOtherPet: !!sitter?.willSitOtherPet,
    willSitSenior: !!sitter?.willSitSenior,
    willSitHome: !!sitter?.willSitHome,
  });
  const [options, setOptions] = useState<YearExperience[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.title = 'UrbanSitter - Who You Work With';
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async (): Promise<void> => {
      try {
        const resp = await YearExperienceService.getByFilter();
        if (cancelled) return;
        setOptions(resp.data?.data ?? []);
      } catch (err) {
        console.warn('[WorksWith] year-experiences load failed', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return (): void => {
      cancelled = true;
    };
  }, []);

  const canSubmit = ((): boolean => {
    if (submitting) return false;
    const hasAge = AGE_KEYS.some((k) => {
      const cur = experiences[k.key];
      return cur?.id && cur.id !== NOT_WILLING_ID;
    });
    return hasAge || Object.values(toggles).some(Boolean);
  })();

  const submit = async (): Promise<void> => {
    if (!sitter?.id || !canSubmit) return;
    setSubmitting(true);
    try {
      await SitterService.patch(sitter.id, {
        id: sitter.id,
        experiences,
        ...toggles,
      } as never);
      await stores.sitter.refreshSitter?.();
      navigate('/sitter/registration/rates');
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.data?.errors?.[0]?.message) {
        toast.error(e.response.data.errors[0].message);
      } else {
        toast.error(t('pages.worksWith.submitFailed'));
      }
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className='container py-8'>
        <p className='text-neutral-600'>{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div className='relative overflow-hidden md:min-h-[700px]'>
      <div
        aria-hidden='true'
        className='hidden md:block absolute top-0 left-0 h-full w-[40%] bg-no-repeat bg-left-top bg-contain pointer-events-none'
        style={{ backgroundImage: `url(${SITTER_DOG_BG})` }}
      />
      <div className='relative container py-8 md:pl-0 md:pr-4'>
        <div className='md:ml-[41.6667%] md:w-[58.3333%]'>
          <h1 className='text-2xl font-semibold mb-6'>{t('pages.worksWith.heading')}</h1>

          <div className='grid grid-cols-1 sm:grid-cols-2 gap-8'>
            <section>
              <h2 className='text-lg font-semibold mb-2'>{t('pages.worksWith.ageSection')}</h2>
              <p className='mb-5 text-sm text-neutral-700'>
                {t('pages.worksWith.ageInstructions')}
              </p>
              <div className='space-y-4'>
                {AGE_KEYS.map(({ key, labelKey, rangeKey }) => {
                  const current = experiences[key];
                  return (
                    <div key={key}>
                      <label htmlFor={`sitter-exp-${key}`} className='block mb-1 text-sm'>
                        <b>{t(labelKey)}</b> {t(rangeKey)}
                      </label>
                      <select
                        id={`sitter-exp-${key}`}
                        className='w-full border border-neutral-300 rounded px-3 py-2 focus:ring-sapphire focus:border-sapphire'
                        value={current?.id ?? ''}
                        onChange={(e): void => {
                          const id = Number(e.target.value);
                          const option = options.find((o) => o.id === id);
                          setExperiences((prev) => ({
                            ...prev,
                            [key]: option ? { id: option.id, label: option.label, shortLabel: option.shortLabel ?? '' } : undefined,
                          }));
                        }}
                      >
                        <option value=''>{t('pages.worksWith.selectPlaceholder')}</option>
                        {options.map((o) => (
                          <option key={o.id} value={o.id}>{o.label}</option>
                        ))}
                      </select>
                    </div>
                  );
                })}
              </div>
            </section>

            <section>
              <h2 className='text-lg font-semibold mb-3'>{t('pages.worksWith.otherSection')}</h2>
              <div className='space-y-2'>
                {EXTRA_TOGGLES.map(({ field, labelKey }) => (
                  <label key={field} htmlFor={field} className='flex items-center gap-3'>
                    <input
                      id={field}
                      type='checkbox'
                      checked={toggles[field]}
                      onChange={(e): void => setToggles((prev) => ({ ...prev, [field]: e.target.checked }))}
                    />
                    <span>{t(labelKey)}</span>
                  </label>
                ))}
              </div>

              <div className='text-center mt-12'>
                <Button
                  id='continue-button'
                  buttonType={ButtonType.primary}
                  onClick={submit}
                  disabled={!canSubmit}
                >
                  {t('pages.worksWith.continue')}
                </Button>
                <div className='flex justify-center gap-2 mt-4'>
                  {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
                    <span
                      key={i}
                      className={`w-2 h-2 rounded-full inline-block ${
                        i === 4 ? 'bg-sapphire' : 'border border-neutral-300'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
});
